import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import QuotationPublicView from "@/components/quotations/quotation-public-view";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const Route = createFileRoute("/_public/shared/quotations/$id")({
  component: PublicQuotationPage,
});

function PublicQuotationPage() {
  const { id } = Route.useParams();

  // Obtener la cotización pública
  const quotation = useQuery(
    api.companies.quotations.queries.getPublicQuotation,
    {
      id: id as Id<"quotations">,
    },
  );

  // Obtener los items de la cotización
  const quotationItems = useQuery(
    api.companies.quotations.items.queries.getPublicQuotationItemsSorted,
    {
      quotationId: id as Id<"quotations">,
    },
  );

  // Obtener información de la empresa si la cotización existe
  const company = useQuery(
    api.companies.queries.getPublicCompanyWithLogo,
    quotation ? { companyId: quotation.internalControl.companyId } : "skip",
  );

  // Estado de carga
  if (
    quotation === undefined ||
    quotationItems === undefined ||
    company === undefined
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Spinner className="h-8 w-8 mx-auto mb-4" />
          <p className="text-muted-foreground">Cargando cotización...</p>
        </div>
      </div>
    );
  }

  // Cotización no encontrada
  if (quotation === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-destructive">
              Cotización no disponible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                La cotización que buscas no existe o no está disponible para
                visualización pública.
                <br />
                <br />
                Posibles razones:
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>La cotización no ha sido enviada al cliente</li>
                  <li>La cotización ha sido eliminada</li>
                  <li>El enlace ha expirado</li>
                </ul>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cotización encontrada pero no en estado "sent" (esto no debería pasar por la query)
  if (quotation.status !== "sent") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">
              Cotización no disponible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Esta cotización no está disponible para visualización pública.
                <br />
                <br />
                Solo las cotizaciones que han sido enviadas al cliente pueden
                ser visualizadas públicamente.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Todo está bien, mostrar la cotización
  return (
    <div className="min-h-screen bg-background">
      <QuotationPublicView
        quotation={quotation}
        quotationItems={quotationItems || []}
        company={company}
      />
    </div>
  );
}
